import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Link,
  Typography,
  Dialog,
  TextField,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PageHeader from '../PageHeader/PageHeader';
import UserDetailsContext from '../Dashboard/UserDetailsContext';
import axios from 'axios';
import { useExpenseStore } from '../store/useExpenseStore';
import UseFeatureFlags from '../components/UseFeatureFlags';
import { styled } from '@mui/material/styles';

export default function ExpenseApproval() {
  const { userDetails, isLoading, error } = useContext(UserDetailsContext);
  const [expenses, setExpenses] = useState([]);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [comment, setComment] = useState('');
  const [filterStatus, setFilterStatus] = useState('PENDING');
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [submittingAction, setSubmittingAction] = useState(false);
  const [viewingReceiptURL, setViewingReceiptURL] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const [businessInfo, setBusinessInfo] = useState(null);
   const [businessCurrency, setBusinessCurrency] = useState(null);
  const { hasFeature } = UseFeatureFlags();
  const expenseStore = useExpenseStore();
  const StyledTab = styled(Tab)({
  textTransform: 'none',
});
 useEffect(() => {
  const fetchExpenses = async () => {
    if (userDetails?.BusinessID) {
      await expenseStore.loadExpensesByBusiness(userDetails.BusinessID);
      const store = useExpenseStore.getState();
      setExpenses(store.getBusinessExpenses(userDetails.BusinessID));
    }
  };
  fetchExpenses();
}, [userDetails?.BusinessID]);

  const fetchExpensesByBusinessID = async (businessId) => {
    setLoadingExpenses(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/expenses/GetExpensesListByBusinessID`,
        { BusinessID: businessId.toString() }
      );
      const data = response.data;
      if (Array.isArray(data)) {
        setExpenses(data);
      } else {
        console.error('Unexpected response format:', data);
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoadingExpenses(false);
    }
  };

  const handleExpenseAction = (expense, action) => {
   
    setSelectedExpense({ ...expense, action });
  };

  const handleSubmitAction = async () => {
    // Only require comment for REJECT
    if (selectedExpense?.action === 'REJECT' && !comment.trim()) {
      alert('Please enter a comment before rejecting.');
      return;
    }

    setSubmittingAction(true);
    try {
      const { ExpenseID, action } = selectedExpense;
      const TeamContactID = userDetails?.TeamContactID;
      const token = userDetails?.token;

      if (!ExpenseID || !TeamContactID) {
        alert('Missing required data (ExpenseID or TeamContactID). Cannot proceed.');
        return;
      }

      const statusMap = {
        APPROVE: 'approved',
        REJECT: 'rejected',
        PENDING: 'pending',
      };

      const payload = {
        ExpenseID: ExpenseID.toString(),
        TeamContactID: TeamContactID.toString(),
        Status: statusMap[action],
        Comment: comment,
      };

      console.log('📦 Sending payload:', payload);

      const result = await expenseStore.reviewExpense({
        ExpenseID: payload.ExpenseID,
        TeamContactID: payload.TeamContactID,
        Status: payload.Status,
        Comment: payload.Comment,
        BusinessID: userDetails.BusinessID,
      });

      if (result && result.RetString === '0') {
        console.error('❌ API error:', result);
        alert('Failed to update expense: ' + (result.Msg || result.Error || 'Unknown error'));
        return;
      }

      // Refresh local list
      setExpenses(useExpenseStore.getState().getBusinessExpenses(userDetails.BusinessID));
      alert('Expense status updated successfully.');
      handleCloseDialog();
    } catch (error) {
      console.error('Error submitting action:', error);
      alert('Failed to submit expense status');
    } finally {
      setSubmittingAction(false);
    }
  };
  
useEffect(() => {
  const fetchBusinessCurrency = async () => {
    const businessId = userDetails?.BusinessID;
    if (!businessId) return;

    const url = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/Onboard/GetBusinessCurrency`;

    try {
      const response = await axios.post(url, {
        BusinessID: String(businessId),
      });

      

      // Assuming API returns: { Symbol: "₹" }
      setBusinessCurrency(response.data.Symbol);
    } catch (err) {
      console.error('Error fetching business currency:', err);
      
    }
  };

  fetchBusinessCurrency();
}, [userDetails]);

  const handleCloseDialog = () => {
    setSelectedExpense(null);
    setComment('');
  };

  const filteredExpenses = expenses.filter(
    (exp) => exp.ApprovalStatus?.toUpperCase() === filterStatus
  );

  const renderActionButtons = (ApprovalStatus, expense) => {
    const statusActions = {
      PENDING: ['Approve', 'Reject'],
    };


    return statusActions[ApprovalStatus]?.map((action) => (
      <Button
        key={action}
        variant="contained"
        className={`expense-approval-button ${action.toLowerCase()}`}
        onClick={() => handleExpenseAction(expense, action.toUpperCase())}
      >
        {action}
      </Button>
    ));
  };
const getTruncatedFileName = (filename, maxLength = 30) => {
  if (filename.length <= maxLength) return filename;
  const start = filename.slice(0, 15);
  const end = filename.slice(-10);
  return `${start}...${end}`;
};
  return (
    <Box sx={{ p: 3 }}>
      <Box className="page-header-container">
        <PageHeader data="Expense Approval" />
        {error && <Typography color="error">Error: {error}</Typography>}
        {isLoading ? (
          <Typography>Loading user details...</Typography>
        ) : (
          <Tabs
            value={filterStatus}
            onChange={(e, newValue) => setFilterStatus(newValue)}
            variant="scrollable"
            scrollButtons={false}
            className="custom-tabs"
          >
             <StyledTab label="Pending" value="PENDING" />
  <StyledTab label="Approved" value="APPROVED" />
  <StyledTab label="Rejected" value="REJECTED" />
          </Tabs>
        )}
      </Box>

      <Box
  sx={{
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',      // 1 column on mobile
      sm: '1fr',      // 1 column on small screens
      md: 'repeat(3, 1fr)', // 3 columns on medium and up
    },
    gap: '30px',
    mt: 4
  }}
>
        {filteredExpenses.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No expenses to show.
          </Typography>
        ) : (
          filteredExpenses.map((expense) => {
            const {
              ExpenseID,
              ExpenseDate,
              UserName,
              Amount,
              CategoryName,
              ApprovalStatus,
              Description,
              ReceiptURL
            } = expense;

            return (
              <Card
                key={ExpenseID}
                variant="outlined"
                className="expense-card"
              >
                <CardContent className="expense-card-content">
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography  component="div"  sx={{ fontSize: 20}} >
                       <stong> {CategoryName}</stong>
                      </Typography>
                      <Typography color="text.secondary">ID: {ExpenseID}</Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      className={`expense-status-indicator ${ApprovalStatus?.toLowerCase()}`}
                      sx={{
                        bgcolor:
                          ApprovalStatus?.toUpperCase() === 'APPROVED'
                            ? 'success.light'
                            : ApprovalStatus?.toUpperCase() === 'REJECTED'
                            ? 'error.light'
                            : 'warning.light',
                        color: 'black',
                        px: 2,
                        py: 0.5,
                        borderRadius: 2,
                        fontWeight: 500,
                      }}
                    >
                      {ApprovalStatus?.toUpperCase()}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(ExpenseDate).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </Typography>
                  </Box>

                  <Typography variant="h6" sx={{ my: 1 }}>
                    <strong>{businessCurrency}{Amount}</strong>
                  </Typography>

                  {Description && (
                    <Typography variant="body2">
                      <strong>Description:</strong> {Description}
                    </Typography>
                  )}

                  {/* Approval Comment/Reason */}
                  {expense.ApprovalComment && (
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                      <strong>Reason:</strong> {expense.ApprovalComment}
                    </Typography>
                  )}

                  {/* Receipt Link */}
                  {ReceiptURL && ReceiptURL !== 'null' && (
                    <Button
                      size="small"
                      onClick={() => {
                        if (ReceiptURL.toLowerCase().endsWith('.pdf')) {
                          window.open(ReceiptURL, '_blank');
                        } else {
                          setViewingReceiptURL(ReceiptURL);
                        }
                      }}
                      sx={{ mt: 1 ,textTransform: 'none',  fontSize: '0.875rem'}}
                    >
                      View Receipt
                    </Button>
                  )}
                </CardContent>
                {filterStatus === 'PENDING' && (
  <CardActions className="expense-actions">

    <Button
      variant="contained"
      color="success"
      onClick={() => handleExpenseAction(expense, 'APPROVE')}
      sx={{ mr: 1, textTransform: 'none', fontSize: '0.875rem' }}
    >
      Approve
    </Button>
  
    
    <Button
      variant="contained"
      color="error"
      onClick={() => handleExpenseAction(expense, 'REJECT')}
         sx={{  textTransform: 'none', fontSize: '0.875rem' }}
    >
      Reject
    </Button>
   
  </CardActions>
)}

              </Card>
            );
          })
        )}
      </Box>

      <Dialog
        open={!!selectedExpense}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            mx: 2,
            borderRadius: 2,
          },
        }}
      >
        <Box sx={{ p: 3, position: 'relative' }}>
          <IconButton
            onClick={handleCloseDialog}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'var(--text-primary)' }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" sx={{ mb: 2 }}>
            {selectedExpense?.action === 'APPROVE' ? 'Approve' : 'Reject'} Expense
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            margin="normal"
          />
          
         <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
  <Button
    onClick={handleCloseDialog}
    sx={{
      backgroundColor: ' #bd0d1eff',
    
      border: '1px solid #dc3545',
      borderRadius: '0.375rem',
      padding: '0.5rem 1rem',
      textTransform: 'none',
    
        color: 'white',
      
    }}
  >
    Cancel
  </Button>

  <Button
    variant="contained"
    onClick={handleSubmitAction}
    sx={{
      padding: '0.5rem 1rem',
      textTransform: 'none',
    }}
    disabled={submittingAction}
  >
    {submittingAction ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
  </Button>
</Box>

        </Box>
      </Dialog>
      <Dialog
  open={!!viewingReceiptURL && !viewingReceiptURL.toLowerCase().endsWith('.pdf')}
  onClose={() => setViewingReceiptURL(null)}
  fullScreen
  sx={{ backgroundColor: 'rgba(0, 0, 0, 0.9)', zIndex: 1500 }}
  PaperProps={{
    sx: {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
  }}
>
  <IconButton
    onClick={() => setViewingReceiptURL(null)}
    sx={{
      position: 'absolute',
      top: 16,
      right: 10,
      width: 40,
      height: 40,
      borderRadius: '50%',
      color: 'white',
      backgroundColor: 'rgba(0,0,0,0.5)',
      '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
    }}
  >
    ✕
  </IconButton>
  <img
    src={viewingReceiptURL}
    alt="Receipt"
    style={{
      maxWidth: '90%',
      maxHeight: '90%',
      borderRadius: '8px',
      objectFit: 'contain',
      boxShadow: '0 4px 20px rgba(255,255,255,0.2)',
    }}
  />
</Dialog>

    </Box>
  );
}